import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/utils/news";

const navItems = [
  ...categories.map((c) => ({ label: c.name, to: `/category/${c.id}` })),
  { label: "专题", to: "/special/annual-gala" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus();
  }, [searchOpen]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
  };

  const isActive = (to: string) => {
    if (to.startsWith("/category")) return location.pathname === to;
    if (to === "/special/annual-gala") return location.pathname.startsWith("/special");
    return false;
  };

  return (
    <header
      className={`sticky top-0 z-40 w-full transition-colors duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur border-b border-background-200" : "bg-background-50"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
            <i className="ri-newspaper-line text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-lg font-black text-foreground-950 tracking-tight">吃瓜爆料</span>
            <span className="text-[10px] text-foreground-500 mt-0.5 hidden sm:block">娱乐八卦 · 社会热点</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-3.5 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                isActive(item.to)
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden md:flex items-center relative">
            <i className="ri-search-line absolute left-3 text-foreground-400 text-sm" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索爆料、明星、热点"
              className="w-52 lg:w-64 h-9 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </form>

          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            aria-label="搜索"
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-search-line text-lg" />
          </button>

          <Link
            to="/about"
            className="hidden lg:inline-flex items-center h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 cursor-pointer transition-colors"
          >
            关于我们
          </Link>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="菜单"
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="md:hidden px-4 pb-3">
          <form onSubmit={submitSearch} className="flex items-center relative">
            <i className="ri-search-line absolute left-3 text-foreground-400 text-sm" />
            <input
              ref={searchRef}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索爆料、明星、热点"
              className="w-full h-10 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm outline-none focus:border-primary-400"
            />
          </form>
        </div>
      )}

      {open && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-3">
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-3 rounded-md text-sm font-medium ${
                  isActive(item.to) ? "bg-primary-100 text-primary-700" : "text-foreground-700"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link to="/about" className="px-3 py-3 rounded-md text-sm font-medium text-foreground-700">
              关于我们
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}